import { useState } from 'react';
import { isValidEmail } from '@/utils/validate-input';
import { useShowAlertNudge } from '@/store/store';
import { useOptionalLoginModals } from '@/components/Login/common/LoginModalsManager';
import { useLoginPromptStore } from '@/store/login-prompt-store';
import { loginLocal, authErrorMessage } from '@/lib/auth/common/client';
import { finalizeClientLogin } from '@/lib/auth/common/finalize-login';
import { resolvePostLoginUrl } from '@/utils/auth/common/auth-storage';

type FieldErrors = { email?: string; password?: string };

/** Null when email + password are both present and email-shaped; otherwise the field errors plus one message to show above the form. */
function validateLoginInput(email: string, password: string): { errors: FieldErrors; message: string } | null {
  const errors: FieldErrors = {};
  if (!email) errors.email = 'Email is required';
  if (!password) errors.password = 'Password is required';
  // Stricter than the server's check — catches a typo before a round trip.
  if (email && !isValidEmail(email)) errors.email = 'Email is not valid';

  if (Object.keys(errors).length === 0) return null;

  // Precedence: both missing > email > password.
  const message = !email && !password ? 'Email and password is required' : (errors.email ?? errors.password)!;
  return { errors, message };
}

/** A thrown error is a network failure (fetch/timeout/DNS) — bad credentials come back as a status code, not a throw. */
function describeThrownLoginError(err: unknown): string {
  const isNetworkError =
    err instanceof TypeError || (err instanceof Error && /network|fetch|timeout/i.test(err.message));
  return isNetworkError
    ? 'Unable to reach the server. Check your connection and try again.'
    : 'Invalid email or password';
}

export function useLoginForm() {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [errorMsg, setErrorMsg] = useState('');
  // Selectors, not whole-store subscriptions — avoids re-rendering on unrelated writes.
  const setShowAlertNudge = useShowAlertNudge((s) => s.setShowAlertNudge);
  const resetLoginPrompt = useLoginPromptStore((s) => s.resetAll);

  // Optional: null when this form renders outside the modal shell.
  const loginModals = useOptionalLoginModals();

  // Highlights both fields (empty strings = error styling only, no per-field text) and
  // shows one message above the form.
  const failLogin = (message: string) => {
    setErrors({ email: '', password: '' });
    setErrorMsg(message);
  };

  // Adopt the session, clear stale nudges/prompts, close the modal, then hard-navigate —
  // matches SSO/SAML (a soft router.refresh() was tried and dropped, see git history).
  // resolvePostLoginUrl() owns read/validate/clear; no stored path means "stay put".
  const onLoginSuccess = (body: unknown) => {
    finalizeClientLogin(body);
    setShowAlertNudge(false);
    resetLoginPrompt();
    loginModals?.closeLoginModal();

    const redirectUrl = resolvePostLoginUrl();
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    } else {
      window.location.reload();
    }
  };

  const handleLogin = async (email: string, password: string): Promise<void> => {
    setErrors({});
    setErrorMsg('');

    const validation = validateLoginInput(email, password);
    if (validation) {
      setErrors(validation.errors);
      setErrorMsg(validation.message);
      return;
    }

    setLoading(true);
    try {
      // No pre-emptive logout call — the login response's Set-Cookie already replaces any
      // stale session cookie outright.
      const { status, body } = await loginLocal({ email, password });

      if (status === 200 || status === 201) {
        onLoginSuccess(body);
      } else if (status === 400) {
        // Partner-only account — tell them to use "Log In As Partner" instead.
        setErrorMsg(authErrorMessage(body, 'Your account is registered as a partner account. Please Log In as a Partner instead.'));
      } else if (status === 429) {
        // Defensive: this app no longer throttles, but the Academy API may.
        failLogin(authErrorMessage(body, 'Too many attempts. Try again later.'));
      } else {
        failLogin(authErrorMessage(body, 'Invalid email or password'));
      }
    } catch (err) {
      failLogin(describeThrownLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  const clearErrors = () => {
    setErrors({});
    setErrorMsg('');
  };

  return {
    loading,
    errors,
    errorMsg,
    handleLogin,
    clearErrors,
  };
}

